import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_TRANSACTION_KPI } from 'api/graphql/queries/transactionKpiQuery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShoppingCart, 
  faArrowTrendUp,
  faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';

interface TransactionTotals {
  total_sales: number;
  total_earnings: number;
  total_commission: number;
  total_withdrawal: number;
  total_payment: number;
}

const TransactionSalesSummary: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Handler to navigate to transactions page
  const handleNavigateToTransactions = () => {
    navigate('/transactions');
  };
  const [transactionTotals, setTransactionTotals] = useState<TransactionTotals>({
    total_sales: 0,
    total_earnings: 0,
    total_commission: 0,
    total_withdrawal: 0,
    total_payment: 0
  });
  
  const [animateCounters, setAnimateCounters] = useState(false);
  
  // Using all-time data (not filtering by days)
  const { loading, error, data } = useQuery(GET_TRANSACTION_KPI, {
    fetchPolicy: 'network-only'
  });
  
  useEffect(() => {
    if (data?.getTransactionKpi?.transaction_totals) {
      setTransactionTotals(data.getTransactionKpi.transaction_totals);
      setTimeout(() => setAnimateCounters(true), 300);
    }
  }, [data]);
  
  // Format number as currency
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('fr-TN', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ` ${t('transactionSummary.dinarSymbol')}`;
  };
    
  // Calculate earnings percentage of total sales
  const earningsPercentage = transactionTotals.total_sales > 0 
    ? (transactionTotals.total_earnings / transactionTotals.total_sales) * 100 
    : 0;
  
  // Counter animation
  const AnimatedCounter: React.FC<{value: number, format: (n: number) => string}> = ({ value, format }) => {
    const [displayValue, setDisplayValue] = useState(0);
    
    useEffect(() => {
      if (animateCounters && value > 0) {
        const duration = 2000; // 2 seconds
        const framesPerSecond = 60;
        const totalFrames = duration / 1000 * framesPerSecond;
        const increment = value / totalFrames;
        
        let frame = 0;
        const timer = setInterval(() => {
          frame++;
          setDisplayValue(prev => {
            const newValue = prev + increment;
            if (newValue >= value || frame >= totalFrames) {
              clearInterval(timer);
              return value;
            }
            return newValue;
          });
        }, 1000 / framesPerSecond);
        
        return () => clearInterval(timer);
      } else {
        setDisplayValue(value);
      }
    }, [value, animateCounters]);
    
    return <span>{format(displayValue)}</span>;
  };
  // Common style for metric icons
  const iconBoxStyle = {
    width: '32px', 
    height: '32px', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.375rem',
    marginLeft: '0.5rem'
  };
  
  return (
    <Card 
      className="h-100 shadow-sm border-0 overflow-hidden cursor-pointer"
      style={{
        background: 'var(--phoenix-card-bg, linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%))',
        borderRadius: '0.75rem'
      }}
      onClick={handleNavigateToTransactions}
    >
      <Card.Header className="p-3 d-flex flex-column justify-content-center">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 fw-bold text-body">
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
            {t('transactionSummary.title')}
          </h5>
          <small className="text-primary">
            {t('viewDetails')} →
          </small>
        </div>
      </Card.Header>
      <Card.Body className="pt-2 pb-3 px-3 d-flex flex-column justify-content-center">
        
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-grow text-primary" style={{ width: '1.5rem', height: '1.5rem' }} role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : (
          <>
            {/* Main highlight stats */}
            <div className="row g-2 mb-2">
              <div className="col-12">
                <div 
                  className="p-2 rounded-3 d-flex flex-column"
                  style={{
                    background: 'linear-gradient(135deg, rgba(13,110,253,0.15) 0%, rgba(13,110,253,0.05) 100%)',
                    border: '1px solid rgba(13,110,253,0.1)'
                  }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="fs-9 text-primary-emphasis fw-medium">{t('totalSalesRevenue')}</div>
                      <div style={{
                        ...iconBoxStyle,
                        background: 'rgba(13,110,253,0.1)',
                        marginLeft: '0.5rem',
                        marginRight: 0
                      }}>
                        <FontAwesomeIcon icon={faShoppingCart} className="text-primary" size="sm" />
                      </div>
                    </div>
                    <div className="fs-5 fw-bold text-primary lh-1">
                      <AnimatedCounter value={transactionTotals.total_sales} format={formatCurrency} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div 
                  className="p-2 rounded-3 d-flex flex-column"
                  style={{
                    background: 'linear-gradient(135deg, rgba(25,135,84,0.15) 0%, rgba(25,135,84,0.05) 100%)',
                    border: '1px solid rgba(25,135,84,0.1)'
                  }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="fs-9 text-success-emphasis fw-medium">{t('totalEarnings')}</div>
                      <div style={{
                        ...iconBoxStyle,
                        background: 'rgba(25,135,84,0.1)',
                        marginLeft: '0.5rem',
                        marginRight: 0
                      }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-success" size="sm" />
                      </div>
                    </div>
                    <div className="fs-5 fw-bold text-success lh-1">
                      <AnimatedCounter value={transactionTotals.total_earnings} format={formatCurrency} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Earnings as percentage of sales */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fs-sm fw-medium">{t('earningsSalesRatio')}</span>
                <span className="fs-sm fw-semibold text-success">{earningsPercentage.toFixed(1)}%</span>
              </div>
              <ProgressBar 
                now={earningsPercentage} 
                className="rounded-pill" 
                style={{ height: '8px' }}
                variant="success" 
              />
              <div className="text-center mt-3">
                <small className="text-body-secondary">
                  <FontAwesomeIcon icon={faArrowTrendUp} className="me-1 text-success" />
                  {t('lifetimeSalesPerformance')}
                </small>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default TransactionSalesSummary;
